import { PaginationData, post, postData } from '../../util/Api';
import { Invoice } from '../InvoiceModel';
import { User } from '../UserModel';
import { DailyReportComment } from './DailyReportCommentModel';

export interface DailyReport {
  id: number | null;
  created_at: Date | null;
  updated_at: Date | null;
  deleted_at: Date | null;
  user_id: number;
  company_id: number;
  ymd: string;
  memo: string;
  invoices: Invoice[];
  user: User | null;
  comments: DailyReportComment[];
  is_read?: boolean;
}

// DailyReport class implementing the interface
export class DailyReportModel implements DailyReport {
  id: number | null;
  created_at: Date | null;
  updated_at: Date | null;
  deleted_at: Date | null;
  user_id: number;
  company_id: number;
  ymd: string;
  memo: string;
  invoices: Invoice[];
  user: User | null;
  comments: DailyReportComment[];
  is_read?: boolean;

  constructor(data: DailyReport) {
    this.id = data.id;
    this.created_at = data.created_at;
    this.updated_at = data.updated_at;
    this.deleted_at = data.deleted_at;
    this.user_id = data.user_id;
    this.company_id = data.company_id;
    this.ymd = data.ymd;
    this.memo = data.memo;
    this.invoices = data.invoices;
    this.user = data.user;
    this.comments = data.comments;
    this.is_read = data.is_read;
  }

  getFormattedYmd(): string {
    const year = this.ymd.substring(0, 4);
    const month = this.ymd.substring(4, 6);
    const day = this.ymd.substring(6, 8);
    return `${year}-${month}-${day}`;
  }

  // Static API methods
  static async fetchById(id: number): Promise<DailyReport> {
    const response = await fetch(`/api/daily_reports/${id}`);
    const data = await response.json();
    return new DailyReportModel({
      ...data,
      createdAt: data.createdAt ? new Date(data.createdAt) : null,
      updatedAt: data.updatedAt ? new Date(data.updatedAt) : null,
      deletedAt: data.deletedAt ? new Date(data.deletedAt) : null,
    });
  }

  static async fetchByYmd(params: { ymd: string }): Promise<DailyReport | null> {
    const uri = `/daily_reports/find`;
    const response = await postData(uri, params);

    const data = response.data;
    if (data === null) {
      return null;
    }

    return new DailyReportModel({
      ...data,
      createdAt: data.createdAt ? new Date(data.createdAt) : null,
      updatedAt: data.updatedAt ? new Date(data.updatedAt) : null,
      deletedAt: data.deletedAt ? new Date(data.deletedAt) : null,
    });
  }

  static async fetchList(params: {
    page: number;
    user_ids: string[];
    member_type: string;
    only_unread: boolean;
  }): Promise<PaginationData<DailyReport>> {
    const uri = `/daily_reports/list`;

    try {
      const response = await postData(uri, params);
      const { data, current_page, last_page, total } = response.data;

      // ページング情報と共にデータを返却
      return {
        data: data.map(
          (item: any) =>
            new DailyReportModel({
              ...item,
              createdAt: item.createdAt ? new Date(item.createdAt) : null,
              updatedAt: item.updatedAt ? new Date(item.updatedAt) : null,
              deletedAt: item.deletedAt ? new Date(item.deletedAt) : null,
            })
        ),
        currentPage: current_page,
        lastPage: last_page,
        total: total,
      };
    } catch (error) {
      console.error('Failed to fetch daily reports list:', error);
      throw error;
    }
  }

  static async fetchAll(): Promise<DailyReport[]> {
    const uri = `/daily_reports/find`;

    const response = await postData(uri, this);

    const data = await response.data;
    return data.map(
      (item: any) =>
        new DailyReportModel({
          ...item,
          createdAt: item.createdAt ? new Date(item.createdAt) : null,
          updatedAt: item.updatedAt ? new Date(item.updatedAt) : null,
          deletedAt: item.deletedAt ? new Date(item.deletedAt) : null,
        })
    );
  }

  async save(): Promise<void> {
    const uri = `/daily_reports/save`;
    let response = await postData(uri, this);
  }

  /**
   * 日報を削除する
   */
  static async delete(dailyReport: DailyReport): Promise<void> {
    await postData(`/daily-report/${dailyReport.id}/delete`);
  }
}
